import { DestroyRef, inject, Injectable } from '@angular/core';
import { Map } from 'maplibre-gl';
import { BannerService } from '@components/layout/banner.service';
import { FCD_VECTOR_STYLE } from '@pages/overview-page/layers/vector-layers';
import { LastUpdateAgeMonitor, maxTimestamp } from '@services/data/last-update-age-monitor';

const FCD_SOURCE_ID = FCD_VECTOR_STYLE.id + '-fcd';
const FCD_SOURCE_LAYER = 'public.fcd_actual';

/**
 * Single owner of the FCD tile-staleness warning. The traffic, incident and roadworks maps are all
 * root singletons that add the same FCD source and share one banner; running a monitor per map
 * would have them clobber each other's banner. Instead one monitor watches whichever map most
 * recently loaded (i.e. the page the user is on).
 */
@Injectable({ providedIn: 'root' })
export class FcdFreshnessService {
  private readonly bannerService = inject(BannerService);
  private readonly destroyRef = inject(DestroyRef);

  private activeMap?: Map;
  private monitor?: LastUpdateAgeMonitor;

  /** Point the freshness watch at the map that just loaded (the active page's map). */
  watch(map: Map): void {
    this.activeMap = map;
    this.monitor ??= new LastUpdateAgeMonitor(
      'fcd',
      {
        label: 'verkeersdata',
        lastUpdatedTimestamp: () => this.newestTileTimestamp(),
        maxAgeMinutesDaytime: 5,
        maxAgeMinutesNighttime: 5,
        bannerTitle: 'Verouderde verkeersdata',
        bannerMessage: (minutesAgo) =>
          `De getoonde actuele verkeersdata is ${minutesAgo} minuten oud. Ververs de pagina of neem contact op met de servicedesk als het probleem blijft bestaan.`,
      },
      this.bannerService,
      this.destroyRef,
    );
    this.monitor.start();
  }

  private newestTileTimestamp(): number {
    const map = this.activeMap;
    if (!map) {
      return 0;
    }
    try {
      const features = map.querySourceFeatures(FCD_SOURCE_ID, { sourceLayer: FCD_SOURCE_LAYER });
      return maxTimestamp(features, (feature) => feature.properties?.['timestamp']);
    } catch {
      // The map may have been removed between navigation and the next style.load; skip this tick.
      return 0;
    }
  }
}
